import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Grid,
  TextField,
  Button,
  Avatar,
  CardHeader,
} from '@material-ui/core';
import applicationLogo from '../../design/zeiss-logo-tagline_rgb.png';
import {
  loginWithCredentials,
  initiateSAMLSSO,
  finalizeSAMLSSO,
} from '../../services/authService';
import { getErrorMessage } from '../../utils/errorHandler';
import { initializeSession } from '../../services/sessionService';

// ============================================
// DEBUG FLAG - Set to false for production (SSO only)
// Set to true for development (show email/password login)
// ============================================
const SHOW_EMAIL_PASSWORD_LOGIN = false;  // Change to true for debug

const styles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputRoot: {
    fontSize: 20,
    color: 'rgba(0,0,0,0.9)',
  },
  input: {
    '&::placeholder': {
      color: 'black',
      boxShadow: 'none',
    },
  },
  ssoButton: {
    marginRight: '10px',
    marginBottom: '10px',
  },
  submitButton: {
    marginTop: '10px',
  },
  imgLogoSignIn: {
    width: '100%',
  },
  large: {
    backgroundColor: 'white',
    width: theme.spacing(10),
    height: theme.spacing(10),
  },
  errorMessage: {
    color: 'red',
    marginBottom: '10px',
    padding: '10px',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    borderRadius: '4px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
});

class SignInContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      error: null,
      isLoading: false,
      isSSO: false,
    };
  }

  componentDidMount() {
    this.checkForSSOCallback();
  }

  /**
   * Check for SSO callback in URL parameters
   */
  checkForSSOCallback = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get('error');
    const sessionIndex = queryParams.get('session_index');

    if (error) {
      this.setState({ error: decodeURIComponent(error) });
    } else if (sessionIndex) {
      this.finalizeSSOLogin(sessionIndex);
    }
  };

  /**
   * Handle email input change
   */
  handleEmailChange = (e) => {
    this.setState({ email: e.target.value });
  };

  /**
   * Handle password input change
   */
  handlePasswordChange = (e) => {
    this.setState({ password: e.target.value });
  };

  /**
   * Handle traditional login with email/password
   */
  handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { email, password } = this.state;

    if (!email || !password) {
      this.setState({ error: 'Email and password are required' });
      return;
    }

    this.setState({ isLoading: true, error: null });

    try {
      const response = await loginWithCredentials(email, password);

      if (response.error) {
        this.setState({ error: response.error });
      } else if (response.token) {
        this.props.history.push('/inputs');
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      this.setState({ error: errorMessage });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /**
   * Initiate SAML SSO login
   */
  handleSSO = async () => {
    this.setState({ isLoading: true, isSSO: true, error: null });

    try {
      const response = await initiateSAMLSSO();
      if (response.sso_url) {
        window.location.href = response.sso_url;
      } else {
        this.setState({ error: 'Failed to initiate SSO' });
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      this.setState({ error: errorMessage });
    } finally {
      this.setState({ isLoading: false, isSSO: false });
    }
  };

  /**
   * Finalize SAML SSO login
   */
  finalizeSSOLogin = async (sessionIndex) => {
    this.setState({ isLoading: true });

    try {
      const response = await finalizeSAMLSSO(sessionIndex);

      if (response.token) {
        initializeSession(response.token);
        this.props.history.push('/inputs');
      } else {
        this.setState({ error: 'SSO finalization failed' });
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      this.setState({ error: errorMessage });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { email, password, error, isLoading } = this.state;

    return (
      <div className={classes.wrapper}>
        <div className="page_header">
          <div className="page page400">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={12}>
                <CardHeader
                  avatar={
                    <Avatar
                      variant="square"
                      alt="ripple-simulator"
                      src={applicationLogo}
                      className={classes.large}
                    />
                  }
                  titleTypographyProps={{
                    variant: 'h4',
                    color: 'textPrimary',
                  }}
                  title="Ripple Simulator"
                />
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="page page400 pageBody">
          {error && (
            <div className={classes.errorMessage} role="alert">
              {error}
            </div>
          )}

          <form className={classes.container} onSubmit={this.handleLogin}>
            {/* SSO Button - Always visible */}
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              className={classes.ssoButton}
              onClick={this.handleSSO}
              disabled={isLoading}
            >
              Sign in with your Zeiss Account
            </Button>

            {/* Email/Password fields - Only visible when DEBUG flag is true */}
            {SHOW_EMAIL_PASSWORD_LOGIN && (
              <>
                <TextField
                  id="email-input"
                  label="Email"
                  placeholder="Enter your email"
                  type="email"
                  autoComplete="email"
                  fullWidth
                  value={email}
                  onChange={this.handleEmailChange}
                  margin="normal"
                  disabled={isLoading}
                  InputLabelProps={{
                    classes: { root: classes.inputRoot },
                    shrink: true,
                  }}
                  InputProps={{
                    classes: { input: classes.input },
                  }}
                />

                <TextField
                  id="password-input"
                  label="Password"
                  placeholder="Enter your password"
                  type="password"
                  autoComplete="current-password"
                  fullWidth
                  value={password}
                  onChange={this.handlePasswordChange}
                  margin="normal"
                  disabled={isLoading}
                  InputLabelProps={{
                    classes: { root: classes.inputRoot },
                    shrink: true,
                  }}
                  InputProps={{
                    classes: { input: classes.input },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  className={classes.submitButton}
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(SignInContainer);